import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, buildMediaUrl, API_BASE } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { ArrowLeft, Calendar, MapPin, AlertTriangle, FileText, Share2, Eye } from "lucide-react";
import { toast } from "sonner";

// Render article content. New articles store HTML from the rich editor;
// older articles may still contain plain text with newlines — preserve those.
function renderContent(raw) {
  if (!raw) return "";
  const looksLikeHtml = /<(p|h1|h2|h3|h4|h5|h6|ul|ol|li|blockquote|br|span|div|strong|em|b|i|u|a)\b/i.test(raw);
  if (looksLikeHtml) return raw;
  const esc = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { t, pickArticle, lang, languages, setLang } = useLang();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-[800px] mx-auto px-5 py-20 text-center text-slate500" data-testid="article-loading">
        {t.common.loading}
      </div>
    );
  }
  if (!article) {
    return (
      <div className="max-w-[800px] mx-auto px-5 py-20 text-center" data-testid="article-not-found">
        <h1 className="heading-serif text-3xl text-ink mb-4">404</h1>
        <button onClick={() => navigate(-1)} className="text-ink underline">{t.article.back}</button>
      </div>
    );
  }

  const block = pickArticle(article);
  const date = new Date(article.publish_date);
  const dateStr = date.toLocaleDateString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-GB", { year: "numeric", month: "long", day: "2-digit" });
  const featured = article.featured_image ? buildMediaUrl(article.featured_image) : null;
  const cat = t.categories[article.category] || article.category;

  // For social sharing (WhatsApp, Twitter, Facebook, LinkedIn) we use the
  // server-rendered share URL because it carries proper per-article Open
  // Graph tags (og:title / og:description / og:image). The SPA URL only
  // has generic site-wide tags so previews on WhatsApp/FB/Twitter look empty.
  const shareUrl = `${API_BASE}/articles/${article.slug}/share?lang=${lang}`;

  const share = (network) => {
    const url = shareUrl;
    const title = block.title;
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title}\n\n${url}`)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    };
    window.open(map[network], "_blank", "width=600,height=600");
  };

  return (
    <div data-testid="article-detail">
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 pt-10 pb-4">
        <Link to="/" className="text-[12px] uppercase tracking-widest text-slate500 hover:text-ink inline-flex items-center gap-1 link-underline">
          <ArrowLeft className="w-3.5 h-3.5" /> {t.article.back}
        </Link>
      </div>

      <article className="max-w-[1100px] mx-auto px-5 lg:px-10 pb-20">
        <div className="flex items-center gap-3 mb-6 mt-4">
          <span className="label-eyebrow text-ink">{cat}</span>
          {article.is_breaking && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-crimson">
              <AlertTriangle className="w-3 h-3" /> Breaking
            </span>
          )}
        </div>

        <h1 className="heading-serif text-4xl lg:text-[56px] leading-[1.05] text-ink mb-7 max-w-4xl">
          {block.title}
        </h1>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-[12px] text-slate600 uppercase tracking-wider">
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> {dateStr}
          </span>
          <span className="inline-flex items-center gap-1" data-testid="article-views">
            <Eye className="w-3.5 h-3.5" /> {(article.views || 0).toLocaleString(lang === "ar" ? "ar-EG" : lang === "fr" ? "fr-FR" : "en-GB")} {t.article.views_label || "views"}
          </span>
          {article.country && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {article.country}
            </span>
          )}
          {article.organization && <span>{t.article.organization}: {article.organization}</span>}
          {article.region && <span>{t.article.region}: {article.region}</span>}
        </div>

        {/* Language switcher */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate200">
          <div className="flex items-center gap-2" data-testid="article-language-switcher">
            <span className="label-eyebrow text-slate500">{t.common.lang_label}</span>
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`article-lang-${l.code}`}
                disabled={!article.translations?.[l.code]?.title}
                className={`px-2.5 h-7 text-[12px] border ${lang === l.code ? "bg-ink text-white border-ink" : "border-slate200 text-slate600 hover:border-ink hover:text-ink"} disabled:opacity-30`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="label-eyebrow text-slate500 hidden md:inline">{t.article.share}</span>
            <button onClick={() => share("whatsapp")} data-testid="share-whatsapp" title="WhatsApp" className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"><span className="text-[10px] font-bold">WA</span></button>
            <button onClick={() => share("telegram")} data-testid="share-telegram" title="Telegram" className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"><span className="text-[10px] font-bold">TG</span></button>
            <button onClick={() => share("twitter")} data-testid="share-twitter" title="X (Twitter)" className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"><span className="text-[10px] font-bold">X</span></button>
            <button onClick={() => share("linkedin")} data-testid="share-linkedin" title="LinkedIn" className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"><span className="text-[10px] font-bold">in</span></button>
            <button onClick={() => share("facebook")} data-testid="share-facebook" title="Facebook" className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"><span className="text-[10px] font-bold">f</span></button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl).catch(() => {});
                toast.success(t.article.share_link_copied || "Share link copied — preview will appear with image & title");
              }}
              data-testid="share-copy"
              title={t.article.copy_share_link || "Copy share link"}
              className="p-2 border border-slate200 hover:border-ink hover:bg-ink hover:text-white transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {featured && (
          <div className="mb-10 border border-slate200 overflow-hidden">
            <img src={featured} alt={block.title} className="w-full h-auto object-cover" />
          </div>
        )}

        {block.summary && (
          <p className="text-[18px] leading-[1.7] text-slate700 mb-10 max-w-3xl border-l-2 border-ink ps-5 italic">
            {block.summary}
          </p>
        )}

        <div
          className="prose-mesc max-w-3xl"
          dangerouslySetInnerHTML={{ __html: renderContent(block.content) }}
        />


        {/* Gallery */}
        {Array.isArray(article.gallery_images) && article.gallery_images.length > 0 && (
          <div className="mt-14">
            <div className="label-eyebrow text-ink mb-4">{t.article.gallery}</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {article.gallery_images.map((u, i) => (
                <a key={i} href={buildMediaUrl(u)} target="_blank" rel="noreferrer" className="block aspect-[4/3] border border-slate200 overflow-hidden">
                  <img src={buildMediaUrl(u)} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Videos */}
        {Array.isArray(article.videos) && article.videos.length > 0 && (
          <div className="mt-14">
            <div className="label-eyebrow text-ink mb-4">{t.article.videos}</div>
            <div className="grid md:grid-cols-2 gap-5">
              {article.videos.map((v, i) => (
                <video key={i} src={buildMediaUrl(v)} controls className="w-full border border-slate200" />
              ))}
            </div>
          </div>
        )}

        {/* PDFs */}
        {Array.isArray(article.pdfs) && article.pdfs.length > 0 && (
          <div className="mt-14">
            <div className="label-eyebrow text-ink mb-4">{t.article.attachments}</div>
            <div className="grid gap-3 max-w-2xl">
              {article.pdfs.map((p, i) => (
                <a key={i} href={buildMediaUrl(p.url)} target="_blank" rel="noreferrer" className="flex items-center justify-between border border-slate200 px-5 h-14 hover:border-ink transition-colors">
                  <span className="flex items-center gap-3">
                    <FileText className="w-4 h-4" />
                    <span className="text-[14px] text-ink">{p.name || `Attachment ${i + 1}`}</span>
                  </span>
                  <span className="text-[11px] uppercase tracking-widest text-slate500">{t.article.download_pdf}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="mt-14 flex flex-wrap items-center gap-2">
            <span className="label-eyebrow text-slate500">{t.article.tags}:</span>
            {article.tags.map((tag) => (
              <span key={tag} className="border border-slate200 px-3 h-8 inline-flex items-center text-[12px] text-slate700">{tag}</span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
